import { webglTileRenderer } from './webgl-tile-renderer.js'

const PROGRAM_KEY_PREPARE = 'sun-exposure-prepare'
const PROGRAM_KEY_DISPLAY_GRADIENT = 'sun-exposure-display-gradient'
const PROGRAM_KEY_DISPLAY_BINARY = 'sun-exposure-display-binary'

const FRAGMENT_PREPARE = `
precision highp float;
varying vec2 v_texCoord;
uniform sampler2D u_dem;
uniform float u_cellSize;
uniform vec2 u_pixelSize;
uniform float u_tileSize;
uniform float u_paddedSize;
uniform float u_sunAzimuth;
uniform float u_sunAltitude;

float decodeTerrarium(vec4 texel) {
  return texel.r * 255.0 * 256.0 + texel.g * 255.0 + texel.b * 255.0 / 256.0 - 32768.0;
}

void main() {
  vec2 demUV = (v_texCoord * u_tileSize + u_tileSize) / u_paddedSize;

  if (u_sunAltitude <= 0.0) {
    gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
    return;
  }

  float centerElev = decodeTerrarium(texture2D(u_dem, demUV));

  float dx = sin(u_sunAzimuth);
  float dy = -cos(u_sunAzimuth);
  vec2 stepDir = vec2(dx, dy) * u_pixelSize;

  float maxAngle = 0.0;
  for (int i = 1; i <= 32; i++) {
    vec2 sampleUV = demUV + stepDir * float(i * 2);
    if (sampleUV.x < 0.0 || sampleUV.x > 1.0 || sampleUV.y < 0.0 || sampleUV.y > 1.0) break;
    float elev = decodeTerrarium(texture2D(u_dem, sampleUV));
    float dist = float(i * 2) * u_cellSize;
    float angle = atan(elev - centerElev, dist);
    maxAngle = max(maxAngle, angle);
  }

  float zTL = decodeTerrarium(texture2D(u_dem, demUV + vec2(-u_pixelSize.x, -u_pixelSize.y)));
  float zT  = decodeTerrarium(texture2D(u_dem, demUV + vec2(          0.0, -u_pixelSize.y)));
  float zTR = decodeTerrarium(texture2D(u_dem, demUV + vec2( u_pixelSize.x, -u_pixelSize.y)));
  float zL  = decodeTerrarium(texture2D(u_dem, demUV + vec2(-u_pixelSize.x,           0.0)));
  float zR  = decodeTerrarium(texture2D(u_dem, demUV + vec2( u_pixelSize.x,           0.0)));
  float zBL = decodeTerrarium(texture2D(u_dem, demUV + vec2(-u_pixelSize.x,  u_pixelSize.y)));
  float zB  = decodeTerrarium(texture2D(u_dem, demUV + vec2(          0.0,  u_pixelSize.y)));
  float zBR = decodeTerrarium(texture2D(u_dem, demUV + vec2( u_pixelSize.x,  u_pixelSize.y)));
  float dzdx = ((zTR + 2.0 * zR + zBR) - (zTL + 2.0 * zL + zBL)) / (8.0 * u_cellSize);
  float dzdy = ((zBL + 2.0 * zB + zBR) - (zTL + 2.0 * zT + zTR)) / (8.0 * u_cellSize);
  float len = sqrt(dzdx * dzdx + dzdy * dzdy + 1.0);
  vec3 normal = vec3(-dzdx / len, -dzdy / len, 1.0 / len);

  float cosAlt = cos(u_sunAltitude);
  vec3 sunDir = vec3(sin(u_sunAzimuth) * cosAlt, -cos(u_sunAzimuth) * cosAlt, sin(u_sunAltitude));
  float dotProduct = dot(sunDir, normal);

  float shadowFade = smoothstep(maxAngle - 0.015, maxAngle + 0.015, u_sunAltitude);
  float exposure = max(0.0, dotProduct) * shadowFade;

  gl_FragColor = vec4(exposure, 0.0, 0.0, 1.0);
}
`

const FRAGMENT_DISPLAY_GRADIENT = `
precision highp float;
varying vec2 v_texCoord;
uniform sampler2D u_exposureMap;

void main() {
  float exposure = texture2D(u_exposureMap, v_texCoord).r;

  vec3 color;
  float alpha = 170.0 / 255.0;

  if (exposure <= 0.0) {
    color = vec3(59.0, 130.0, 246.0);
  } else if (exposure < 0.5) {
    float t = exposure / 0.5;
    color = mix(vec3(59.0, 130.0, 246.0), vec3(234.0, 179.0, 8.0), t);
  } else {
    float t = (exposure - 0.5) / 0.5;
    color = mix(vec3(234.0, 179.0, 8.0), vec3(239.0, 68.0, 68.0), t);
  }

  gl_FragColor = vec4(color / 255.0, alpha);
}
`

const FRAGMENT_DISPLAY_BINARY = `
precision highp float;
varying vec2 v_texCoord;
uniform sampler2D u_exposureMap;

void main() {
  float exposure = texture2D(u_exposureMap, v_texCoord).r;

  float binaryT = smoothstep(0.0, 0.05, exposure);
  vec3 color = mix(vec3(59.0, 130.0, 246.0), vec3(234.0, 179.0, 8.0), binaryT);
  float alpha = 170.0 / 255.0;

  gl_FragColor = vec4(color / 255.0, alpha);
}
`

function metersPerPixel(lat: number, zoom: number, tileSize: number): number {
  return ((156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom)) * (256 / tileSize)
}

function tileToLat(y: number, z: number): number {
  const n = Math.PI - (2 * Math.PI * y) / Math.pow(2, z)
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)))
}

export function renderSunExposureTileGPU(
  paddedDem: Uint8ClampedArray,
  paddedSize: number,
  tileSize: number,
  z: number,
  y: number,
  sunAzimuth: number,
  sunAltitude: number,
  mode: 'gradient' | 'binary'
): Uint8ClampedArray | null {
  if (!webglTileRenderer.available && !webglTileRenderer.init()) return null

  const lat = tileToLat(y + 0.5, z)
  const cellSize = metersPerPixel(lat, z, tileSize)

  // Pass 1: compute exposure into intermediate texture
  const prepareProgram = webglTileRenderer.compileProgram(FRAGMENT_PREPARE, PROGRAM_KEY_PREPARE)
  if (!prepareProgram) return null

  const demTexture = webglTileRenderer.uploadTexture(paddedDem, paddedSize, paddedSize)
  if (!demTexture) return null

  let exposureTexture: WebGLTexture | null = null
  try {
    exposureTexture = webglTileRenderer.renderToTexture(
      prepareProgram,
      {
        u_cellSize: cellSize,
        u_pixelSize: [1 / paddedSize, 1 / paddedSize],
        u_tileSize: tileSize,
        u_paddedSize: paddedSize,
        u_sunAzimuth: sunAzimuth,
        u_sunAltitude: sunAltitude,
      },
      {
        u_dem: { texture: demTexture, unit: 0 },
      },
      tileSize,
      tileSize
    )
  } finally {
    webglTileRenderer.deleteTexture(demTexture)
  }

  if (!exposureTexture) return null

  // Pass 2: read smoothed exposure and apply color ramp
  const displayKey = mode === 'binary' ? PROGRAM_KEY_DISPLAY_BINARY : PROGRAM_KEY_DISPLAY_GRADIENT
  const displaySrc = mode === 'binary' ? FRAGMENT_DISPLAY_BINARY : FRAGMENT_DISPLAY_GRADIENT
  const displayProgram = webglTileRenderer.compileProgram(displaySrc, displayKey)
  if (!displayProgram) {
    webglTileRenderer.deleteTexture(exposureTexture)
    return null
  }

  try {
    return webglTileRenderer.renderToPixels(
      displayProgram,
      {},
      {
        u_exposureMap: { texture: exposureTexture, unit: 0 },
      },
      tileSize,
      tileSize
    )
  } finally {
    webglTileRenderer.deleteTexture(exposureTexture)
  }
}
